import React, { memo, useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import interactionResponse from 'await-interaction-response';
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { getMarket } from '@atc/bonnet-reference';
import { DOMAnimation } from '@atc/dom-fns';
import { useFeatures } from '@atc/features';
import { formatNumber } from '@atc/string-fns';

import {
    Accordion,
    Button,
    Panel,
    SideBar,
    SideBarHeader,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { featureCodeClick, searchRadiusClick } from 'reaxl-analytics-handlers';

// Utilities
import filterComponentsMap from '@/utilities/filterComponentsMap';
import { pauseAudioEye } from '@/utilities/pauseAudioEye';
import { removeCategory } from '@/utilities/searchCategoriesUtils';

import { filterClick } from '@/analytics/srpAnalyticsHandlers';

import paymentsDuck from '@/ducks/paymentsDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import srpContentDuck from '@/ducks/srp/srpContentDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpMarketExtensionDuck from '@/ducks/srp/srpMarketExtensionDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpSimilarListingsDuck from '@/ducks/srp/srpSimilarListingsDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import KeywordSearchContainer from '@/containers/KeywordSearchContainer';
import { KeywordSearchTrigger } from '@/containers/KeywordSearchModalContainer';

import FilterSortDropdownContainer from './FilterSortDropdownContainer';
import InventoryClearFilters from './InventoryClearFilters';
import InventoryFiltersChipsContainer from './InventoryFiltersChipsContainer';
import ModelRebadgeModalContainer from './ModelRebadgeModalContainer';
import SaveSearchContainer from './SaveSearchContainer';

// add additional filter to the navigation query if a certain other filter is selected that elicits multiple values
function getExperienceFilters({ enableBuyOnline = false, event = {}, filter = {} }) {
    const multiValueFilters = ['BUY_ONLINE'];
    const extraFilterValues = {};

    if (enableBuyOnline) {
        if (multiValueFilters.includes(event?.target?.value)) {
            if (filter.value && filter.value?.includes('BUY_ONLINE')) {
                extraFilterValues.experience = '';
            } else {
                extraFilterValues.experience = 'buy-online';
            }
        }
    }
    return extraFilterValues;
}

export function getNewFiltersValues({ filtersValues, target, filter, grouped }) {
    // Create a copy of the existing filtersValues to avoid mutating the original
    const updatedFiltersValues = { ...filtersValues };

    // set is multiple off of elements multiple attribute
    const isMultiple = grouped || target.multiple;
    const {
        type = '',
        checked = false,
        value,
    } = target;

    // Determine if this is an add or remove operation
    let isRemovingValue = false;

    if (type.toLowerCase() === 'checkbox') {
        isRemovingValue = !checked;
    }

    // If this filter doesn't exist yet in filtersValues, initialize it
    if (!updatedFiltersValues[filter.name]) {
        updatedFiltersValues[filter.name] = isMultiple ? [] : '';
    }

    if (isMultiple) {
        // Handle multi-select filters (arrays)
        const currentValues = Array.isArray(updatedFiltersValues[filter.name])
            ? updatedFiltersValues[filter.name]
            : [];

        if (isRemovingValue) {
            // Remove the value from the array
            updatedFiltersValues[filter.name] = currentValues.filter((item) => item !== value);
        } else if (!currentValues.includes(value)) {
            updatedFiltersValues[filter.name] = [...currentValues, value];
        }

        // If array is empty and we're handling a checkbox, set to appropriate value
        if (updatedFiltersValues[filter.name].length === 0 && type.toLowerCase() === 'checkbox' && !isMultiple) {
            updatedFiltersValues[filter.name] = 'off';
        }
    } else {
        // Handle single-select filters (strings)
        updatedFiltersValues[filter.name] = isRemovingValue ? 'off' : value;
    }

    // Combine properties that follow the 'key1|key2' format under 'key1'
    const combinedFiltersValues = { ...updatedFiltersValues };
    const pipeKeysPattern = /^([^|]+)\|(.+)$/;

    Object.keys(updatedFiltersValues).forEach((key) => {
        const match = key.match(pipeKeysPattern);
        if (match) {
            const [, mainKey] = match;

            // Initialize the main key if it doesn't exist yet
            if (!combinedFiltersValues[mainKey]) {
                combinedFiltersValues[mainKey] = [];
            }

            // Convert to array if it's not already
            if (!Array.isArray(combinedFiltersValues[mainKey])) {
                combinedFiltersValues[mainKey] = [combinedFiltersValues[mainKey]].filter((v) => v !== '' && v !== 'off');
            }

            // Add values from the pipe key to the main key
            const pipeKeyValues = updatedFiltersValues[key];
            if (Array.isArray(pipeKeyValues) && pipeKeyValues.length > 0) {
                // Add array values
                combinedFiltersValues[mainKey] = [
                    ...combinedFiltersValues[mainKey],
                    ...pipeKeyValues,
                ];
            } else if (pipeKeyValues && pipeKeyValues !== '' && pipeKeyValues !== 'off') {
                // Add single value if it's not empty or 'off'
                combinedFiltersValues[mainKey].push(pipeKeyValues);
            }

            // Remove the original pipe-format key after combining
            delete combinedFiltersValues[key];
        }
    });

    // Clean up any empty objects or arrays in the combinedFiltersValues
    Object.keys(combinedFiltersValues).forEach((key) => {
        const combinedFiltersValue = combinedFiltersValues[key];

        if (Array.isArray(combinedFiltersValue) && combinedFiltersValue.length === 0) {
            // Delete empty arrays
            delete combinedFiltersValues[key];
        } else if (combinedFiltersValue === Object(combinedFiltersValue) && !Array.isArray(combinedFiltersValue) && Object.keys(combinedFiltersValue).length === 0) {
            // Delete empty objects
            delete combinedFiltersValues[key];
        } else if (combinedFiltersValue === '' || combinedFiltersValue === 'off') {
            // Delete empty strings or 'off' values
            delete combinedFiltersValues[key];
        }
    });

    return combinedFiltersValues;
}

function InventoryFiltersContainer() {
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const dispatch = useDispatch();
    const isFiltersCollapsed = !!_get(device, 'lessThan.md', false);

    const {
        advanced_keyword_search: [enableAdvancedKeywordSearch],
        debounced_filter_navigation: [enableDebouncedFilterNavigation, { wait }],
        filters: [, {
            order: filtersOrder,
        }],
        filter_chips_srp: [enableFilterChips],
        filter_pause_audioeye: [filterPauseAudioEye, { pauseDuration }],
        market_extension: [enableMarketExtension],
        srp_buy_online: [enableBuyOnline],
        srp_category_search: [enableCategorySearch],
    } = useFeatures([
        'advanced_keyword_search',
        'debounced_filter_navigation',
        'filters',
        'filter_chips_srp',
        'filter_pause_audioeye',
        'market_extension',
        'srp_buy_online',
        'srp_category_search',
    ]);

    const isCollapsedFiltersActive = useSelector(srpFiltersDuck.selectors.getFiltersVisible);
    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues);
    const hasFiltersOptions = useSelector(srpFiltersDuck.selectors.hasFiltersOptions);
    const resultCount = useSelector(srpResultsDuck.selectors.getResultsCount);
    const activeCategory = useSelector(srpActiveInteractionDuck.selectors.getActiveCategory, _isEqual);

    // All actively expanded accordion panels
    const activeExpansions = useSelector(srpFiltersDuck.selectors.getActiveExpansions, _isEqual);

    const navigateToSrp = useSrpNavigation();
    const [isProcessing, setIsProcessing] = useState(false);

    const handleShowExpansion = useCallback((event) => {
        if (filterPauseAudioEye) {
            pauseAudioEye(pauseDuration);
        }
        if (!activeExpansions.includes(event)) {
            dispatch(srpFiltersDuck.creators.setActiveExpansion(event));
        }
    }, [activeExpansions, dispatch, filterPauseAudioEye, pauseDuration]);

    const handleHideExpansion = useCallback((event) => {
        dispatch(srpFiltersDuck.creators.removeActiveExpansion(event));
    }, [dispatch]);

    const debouncedNavigateToSrp = _debounce(({ eventId, updatedFiltersValues = {}, action = 'merge' }) => navigateToSrp({
        filtersValues: updatedFiltersValues,
        isNewSearch: true,
        requestId: eventId,
        resetPagination: true,
        scroll: isFiltersCollapsed,
        action,
    }), enableDebouncedFilterNavigation ? wait : 0);

    const applyOptionChange = useCallback(async (event, filter, isGrouped) => {
        if (isProcessing) {
            // eslint-disable-next-line no-console
            console.warn('Filter change already in progress, ignoring rapid click');
            return;
        }

        setIsProcessing(true);

        try {
            // Cancel BEFORE any state mutations
            debouncedNavigateToSrp.cancel();

            const eventId = event.target.id || event.target.value;
            window.sessionStorage.setItem('requestId', eventId);

            const updatedFilters = getNewFiltersValues({ filtersValues, target: event.target, filter, grouped: isGrouped });

            dispatch(srpActiveInteractionDuck.creators.setKeys({
                activeModel: '',
            }));

            dispatch(srpSimilarListingsDuck.creators.setActiveResults([]));

            dispatch(srpContentDuck.creators.clearCrawlPaths());

            const extraFilterValues = getExperienceFilters({ enableBuyOnline, event, filter });

            if (enableCategorySearch) {
                removeCategory({ activeCategory, filterValue: eventId, dispatch });
            }

            await debouncedNavigateToSrp({ eventId, updatedFiltersValues: { ...extraFilterValues, ...updatedFilters }, action: 'replace' });
        } finally {
            setIsProcessing(false);
        }
    }, [debouncedNavigateToSrp, dispatch, filtersValues, enableBuyOnline, enableCategorySearch, activeCategory, isProcessing, setIsProcessing]);

    const handleZipUpdate = useCallback(async (event, filter) => {
        event.persist();

        const { value = '' } = event.target;
        if (value.length === 5) {
            const response = await getMarket(value);

            if (response.success) {
                dispatch(srpFiltersDuck.creators.removeError({ filter }));
                await applyOptionChange(event, filter);
            } else {
                dispatch(srpFiltersDuck.creators.addError({ errorMessage: 'Enter a valid ZIP Code', filter }));
            }
        } else {
            dispatch(srpFiltersDuck.creators.addError({ errorMessage: 'Enter a valid ZIP Code', filter }));
        }
    }, [applyOptionChange, dispatch]);

    // NOTE: Shared between handleOptionChange and handleClearChip methods
    const sendFilterClick = useCallback((event, filter) => {

        if (filter.name === 'searchRadius' || filter.name === 'featureCode' || filter.name === 'zip') {
            return;
        }
        sendClick(filterClick, event, {
            filterName: filter.name,
        });
    }, []);

    const handleOptionChange = useCallback(async (event, filter = {}) => {
        // Cache values immediately to prevent stale closure issues
        const targetValue = event.target.value;
        const targetId = event.target.id;

        if (filterPauseAudioEye) {
            pauseAudioEye(pauseDuration);
        }

        await interactionResponse();

        // Use cached values instead of event.target
        event.target.value = targetValue;
        event.target.id = targetId;

        sendFilterClick(event, filter);

        let isGrouped = false;

        switch (filter.name) {
            case 'makeCode': {
                isGrouped = true;
                break;
            }

            case 'zip': {
                sendClick(searchRadiusClick, event, {
                    filterName: filter.name,
                });
                return handleZipUpdate(event, filter);
            }

            case 'marketExtension': {
                dispatch(srpMarketExtensionDuck.creators.setKey({ value: event.target.value }));
                break;
            }

            case 'maxPrice': {
                dispatch(paymentsDuck.creators.updatePaymentsInfo({ budget: event.target.value }));
                break;
            }

            case 'featureCode': {
                sendClick(featureCodeClick, event, {
                    filterName: filter.name,
                    featureCodeName: event?.target?.nextSibling?.firstChild?.textContent,
                });
                break;
            }

            case 'searchRadius': {
                sendClick(searchRadiusClick, event, {
                    filterName: filter.name,
                });
                // break omitted on purpose
            }
            // eslint-disable-next-line no-fallthrough
            default:
        }

        applyOptionChange(event, filter, isGrouped);
        return true;
    }, [applyOptionChange, dispatch, filterPauseAudioEye, handleZipUpdate, pauseDuration, sendFilterClick]);

    const handleOptionsClear = useCallback(async (event, filter = {}) => {
        if (filterPauseAudioEye) {
            pauseAudioEye(pauseDuration);
        }
        filter.value = [];
        applyOptionChange(event, filter);
    }, [applyOptionChange, filterPauseAudioEye, pauseDuration]);

    const onHideFilters = useCallback(async () => {
        try {
            // Cancel any pending filter navigation to prevent conflicts
            debouncedNavigateToSrp.cancel();

            await interactionResponse();
            await dispatch(srpFiltersDuck.creators.hideFilters());

            // Add small delay to ensure state update is complete before DOM manipulation
            if (isFiltersCollapsed) {
                setTimeout(() => {
                    DOMAnimation.jumpToTopOfPage();
                }, 50);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error hiding filters:', error);
            // Fallback: still try to hide filters even if there's an error
            dispatch(srpFiltersDuck.creators.hideFilters());
        }
    }, [dispatch, isFiltersCollapsed, debouncedNavigateToSrp]);

    useEffect(() => {
        if (!enableMarketExtension) {
            dispatch(srpMarketExtensionDuck.creators.setKey({ value: 'off' }));
        }
    }, [dispatch, enableMarketExtension]);

    const filterProps = {
        onOptionChange: handleOptionChange,
        onOptionsClear: handleOptionsClear,
        handleShowExpansion,
    };

    const sideBarHeaderAction = (
        <Button
            bsStyle="primary"
            className="pull-right"
            onClick={onHideFilters}
        >
            See Results
        </Button>
    );

    const sideBarHeader = (
        <SideBarHeader
            title="Filters"
            onHide={onHideFilters}
            action={sideBarHeaderAction}
        />
    );

    const renderFilters = (
        <Accordion
            className="modular-filters"
            data-cmp="filters"
            defaultActiveKey={activeExpansions}
            onOpen={handleShowExpansion}
            onClose={handleHideExpansion}
        >
            { filtersOrder.map((comp) => React.createElement(filterComponentsMap[comp], { ...filterProps, key: comp })) }

        </Accordion>
    );

    // Hidden Filter elements have an absolutely HUGE cost in memory/performance
    // Don't render or compute anything at all unless they are visible.
    // This does remove the animation of the filters side bar at this time
    return (
        <>
            {hasFiltersOptions && (
                <>
                    <SideBar
                        isDocked={false}
                        show={isCollapsedFiltersActive}
                        onHide={onHideFilters}
                        header={sideBarHeader}
                        fullWidth={isXs}
                    >
                        <Panel className="margin-2">
                            <Panel.Body
                                className="padding-5"
                                data-cmp="cntnr-fltr-selections"
                            >
                                <div
                                    className="display-flex justify-content-between align-items-center"
                                >
                                    <span className="text-size-400 text-bold text-gray-base">Your Search</span>
                                    <span
                                        className="text-size-md-300 text-size-lg-300 text-right text-bold"
                                    >
                                        <SaveSearchContainer key="saveSearchContainer" />
                                    </span>
                                </div>
                                {enableFilterChips && (
                                    <>
                                        <div className="margin-vertical-3">
                                            <InventoryFiltersChipsContainer />
                                        </div>
                                        <div className="text-size-md-300 text-size-lg-300 text-right">
                                            {`${formatNumber({ value: resultCount })} Results`}
                                            <InventoryClearFilters
                                                key="inventoryClearFilters"
                                                collapsed={isFiltersCollapsed}
                                            />
                                        </div>
                                    </>
                                )}
                            </Panel.Body>
                        </Panel>
                        <div className="padding-5">
                            <div className="padding-bottom-2 text-size-400 text-bold text-gray-base">Sort by</div>
                            <FilterSortDropdownContainer />
                        </div>

                        {renderFilters}

                    </SideBar>
                    {!isCollapsedFiltersActive && (
                        <div className="margin-bottom-3">
                            {enableAdvancedKeywordSearch ? <KeywordSearchTrigger /> : <KeywordSearchContainer />}
                        </div>
                    )}

                    {!isCollapsedFiltersActive && renderFilters}
                </>
            )}
            <ModelRebadgeModalContainer />
        </>
    );
}

export default memo(InventoryFiltersContainer);
